// Recorridos: armado mínimo desde el panel (PR 2). Colectas, repartos y
// turbos con sus paradas; asignación de chofer; conteo del depósito;
// horarios de corte por tienda. Sin mapa ni arrastrar y soltar en esta etapa.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

/* ===== Tipos ===== */
type RouteType = "PICKUP" | "DELIVERY" | "TURBO";
type RouteStatus = "PLANNED" | "ASSIGNED" | "LOADED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";

type RouteRow = {
  id: number; type: RouteType; status: RouteStatus; scheduled_date: string;
  driver_id: number | null; driver_username: string | null;
  capacity: number | null; load_expected_count: number | null; load_confirmed_count: number | null;
  stops_count: number; deliveries_count: number; started_at: string | null; completed_at: string | null;
  template_id?: number | null; template_name?: string | null; departure_time?: string | null;
  confirmation_state?: "ACCEPTED" | "DECLINED" | "PENDING" | "OVERDUE" | null;
};
type StopDelivery = { delivery_id: number; tracking: string | number | null; nombre_destinatario: string | null; direccion: string | null; codigo_postal: string | null; ops_status: string; outcome: string | null; outcome_at: string | null };
type Stop = {
  id: number; sequence: number; type: "PICKUP" | "DELIVERY"; status: string; arrived_at: string | null; completed_at: string | null; notes: string | null;
  store_id?: number | null; store_name?: string; store_address?: string; cutoff_snapshot?: string | null; window_snapshot?: number | null; collected_count?: number; store_ready_at?: string | null;
  warehouse_id?: number | null; warehouse_name?: string; warehouse_type?: "CROSSDOCK" | "DROPOFF"; latitude_snapshot?: number | null; longitude_snapshot?: number | null;
  address?: string | null; postal_code?: string | null; receiver_name?: string | null;
  deliveries: StopDelivery[];
};
type RouteDetail = RouteRow & { qr_token: string; notes: string | null; warehouse_id: number | null; driver: { username: string; first_name: string | null; last_name: string | null } | null; stops: Stop[] };
type Store = { id: number; nombre_fantasia: string; direccion?: string | null; codigo_postal?: string | number | null };
type Driver = { id: number; username: string; first_name: string | null; last_name: string | null; vehiculo: string | null };
type WarehouseOption = { id: number; name: string; type: "CROSSDOCK" | "DROPOFF"; address: string | null; latitude: number | null; longitude: number | null; active: boolean };
type Attachable = { id: number; tracking: string | number | null; nombre_destinatario: string | null; direccion: string | null; codigo_postal: string | null; postal_code_norm?: string | null; localidad: string | null; seller: string | null; ops_status: string; geo_status?: string; shelf_id?: number | null; shelf_code?: string | null };
type ShelfOption = { id: number; code: string; name: string; deliveries_count: number; unclassified: boolean };
type PendingItem = { delivery_id: number; tracking: string | number | null; nombre_destinatario: string | null; codigo_postal: string | null; ops_status: string };

const TYPE_LABEL: Record<RouteType, string> = { PICKUP: "Colecta", DELIVERY: "Reparto", TURBO: "Turbo" };
const STATUS_LABEL: Record<RouteStatus, string> = { PLANNED: "Planificado", ASSIGNED: "Asignado", LOADED: "Cargado", IN_PROGRESS: "En calle", COMPLETED: "Completado", CANCELLED: "Cancelado" };
const STOP_STATUS_LABEL: Record<string, string> = { PENDING: "Pendiente", ARRIVED: "Llegó", DONE: "Cerrada", SKIPPED: "Salteada" };
const OPS_LABEL: Record<string, string> = { ENTERED: "Ingresado", COLLECTED: "Colectado", ARRIVED_WAREHOUSE: "En planta", SHELVED: "Estanteado", ASSIGNED_TO_ROUTE: "En recorrido", OUT_FOR_DELIVERY: "En calle", DELIVERED: "Entregado", NOT_DELIVERED: "No entregado", CANCELLED: "Cancelado" };
const today = () => new Date().toISOString().slice(0, 10);
const driverName = (d: { username: string; first_name?: string | null; last_name?: string | null } | null | undefined) =>
  d ? [d.first_name, d.last_name].filter(Boolean).join(" ") || d.username : "—";
// Con cien choferes un desplegable solo no alcanza: se filtra por nombre,
// apellido o usuario, y el usuario se muestra en cada opción (el chofer de
// prueba es «Chofer De Prueba», usuario prueba1: nadie lo encontraba).
function DriverPicker({ drivers, value, onChange, disabled }: { drivers: Driver[]; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const [q, setQ] = useState("");
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? drivers.filter((d) => [d.first_name, d.last_name, d.username].filter(Boolean).join(" ").toLowerCase().includes(t)) : drivers;
  }, [drivers, q]);
  const actual = drivers.find((d) => String(d.id) === value);
  const etiqueta = (d: Driver) => `${driverName(d)} · ${d.username}${d.vehiculo ? ` · ${d.vehiculo}` : ""}`;
  return (
    <div>
      <input style={{ ...selectStyle, minHeight: "36px", marginTop: "6px" }} placeholder="Buscar por nombre o usuario" value={q} onChange={(e) => setQ(e.target.value)} disabled={disabled} />
      <select style={{ ...selectStyle, marginTop: "6px" }} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">Sin asignar</option>
        {actual && !visibles.includes(actual) ? <option value={actual.id}>{etiqueta(actual)}</option> : null}
        {visibles.map((d) => <option key={d.id} value={d.id}>{etiqueta(d)}</option>)}
      </select>
      {q && visibles.length === 0 ? <div style={{ ...smallMutedStyle, marginTop: "4px" }}>Ningún chofer coincide.</div> : null}
    </div>
  );
}

const fmtTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "—");

/* ===== Estilos (mismo lenguaje que Stores.tsx) ===== */
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const selectStyle: React.CSSProperties = { width: "100%", minHeight: "48px", padding: "0 14px", borderRadius: "16px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
// La respuesta del chofer al aviso de la noche anterior (solo rondas fijas).
const CONFIRMATION: Record<string, { label: string; color: string }> = {
  ACCEPTED: { label: "Confirmó", color: "var(--success)" }, DECLINED: { label: "No puede", color: "var(--danger)" },
  PENDING: { label: "Sin confirmar", color: "var(--warning, #d29922)" }, OVERDUE: { label: "Sin confirmar · vencido", color: "var(--danger)" },
};
const statusColor: Record<RouteStatus, string> = { PLANNED: "var(--text-secondary)", ASSIGNED: "var(--warning, #d29922)", LOADED: "var(--warning, #d29922)", IN_PROGRESS: "var(--primary, #4f8cff)", COMPLETED: "var(--success)", CANCELLED: "var(--danger)" };

function Modal({ title, onClose, children, width = "900px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

/* ===== Página ===== */
export default function RoutesPage() {
  const { token } = useAuth();
  const [date, setDate] = useState(() => new URLSearchParams(window.location.search).get("date") || today());
  const [typeFilter, setTypeFilter] = useState<"" | RouteType>("");
  const [statusFilter, setStatusFilter] = useState<"" | RouteStatus>("");
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  // Llama a la API y devuelve el JSON; ok:false o error HTTP se convierten en excepción con el mensaje del servidor.
  const req = useCallback(async (path: string, init: { method?: string; body?: unknown } = {}) => {
    const response = await authFetch(path, { token, method: init.method ?? "GET", json: init.body !== undefined, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.ok === false) throw new Error(json?.error || json?.message || `Error ${response.status}`);
    return json;
  }, [token]);

  const loadRoutes = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams();
      if (date) q.set("date", date);
      if (typeFilter) q.set("type", typeFilter);
      if (statusFilter) q.set("status", statusFilter);
      const json = await req(`/v2/routes?${q.toString()}`);
      setRoutes(json.routes ?? []);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }, [req, date, typeFilter, statusFilter]);

  useEffect(() => { loadRoutes(); }, [loadRoutes]);
  useEffect(() => {
    (async () => {
      try {
        const [s, d, w] = await Promise.all([req("/v2/stores"), req("/v2/routes/drivers"), req("/v2/warehouses")]);
        setStores((s.stores ?? []).map((x: any) => ({ id: Number(x.id), nombre_fantasia: x.nombre_fantasia ?? `Tienda ${x.id}`, direccion: x.direccion, codigo_postal: x.codigo_postal })));
        setDrivers(d.drivers ?? []);
        setWarehouses((w.warehouses ?? []).filter((x: any) => x.active));
      } catch (e: any) { setError(e.message); }
    })();
  }, [req]);

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 4000); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Recorridos</h2>
            <p style={subtitleStyle}>Colectas en tiendas, repartos desde el depósito y turbos. Un reparto no sale hasta que el chofer confirma la misma cantidad que contó el depósito. Los horarios de corte de cada tienda se administran desde Tiendas.</p>
          </div>
        </div>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error}</div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", alignItems: "end", marginBottom: "16px" }}>
          <Input label="Fecha" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <label style={{ display: "block" }}>
            <span style={{ ...smallMutedStyle, display: "block", marginBottom: "8px", fontWeight: 600 }}>Tipo</span>
            <select style={selectStyle} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)}>
              <option value="">Todos</option>
              {(Object.keys(TYPE_LABEL) as RouteType[]).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </label>
          <label style={{ display: "block" }}>
            <span style={{ ...smallMutedStyle, display: "block", marginBottom: "8px", fontWeight: 600 }}>Estado</span>
            <select style={selectStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
              <option value="">Todos</option>
              {(Object.keys(STATUS_LABEL) as RouteStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </label>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button variant="secondary" onClick={loadRoutes} disabled={loading}>{loading ? "Cargando…" : "Actualizar"}</Button>
            <Button onClick={() => setCreating(true)}>Nuevo recorrido</Button>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>#</th><th style={thStyle}>Tipo</th><th style={thStyle}>Fecha</th><th style={thStyle}>Estado</th>
                <th style={thStyle}>Chofer</th><th style={thStyle}>Confirmación</th><th style={thStyle}>Paradas</th><th style={thStyle}>Envíos</th><th style={thStyle}>Carga</th><th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {routes.length === 0 ? (
                <tr><td style={tdStyle} colSpan={10}><span style={smallMutedStyle}>No hay recorridos para esos filtros.</span></td></tr>
              ) : routes.map((r) => (
                <tr key={r.id}>
                  <td style={tdStyle}>{r.id}</td>
                  <td style={tdStyle}>{TYPE_LABEL[r.type]}{r.template_name ? <div style={smallMutedStyle}>{r.template_name} · {r.departure_time?.slice(0, 5)}</div> : null}</td>
                  <td style={tdStyle}>{r.scheduled_date?.slice(0, 10)}</td>
                  <td style={tdStyle}><span style={badge(statusColor[r.status])}>{STATUS_LABEL[r.status]}</span></td>
                  <td style={tdStyle}>{r.driver_username ?? "—"}</td>
                  <td style={tdStyle}>{r.confirmation_state ? <span style={badge(CONFIRMATION[r.confirmation_state].color)}>{CONFIRMATION[r.confirmation_state].label}</span> : <span style={smallMutedStyle}>—</span>}</td>
                  <td style={tdStyle}>{r.stops_count}</td>
                  <td style={tdStyle}>{r.deliveries_count}</td>
                  <td style={tdStyle}>{r.type === "DELIVERY" ? `${r.load_confirmed_count ?? "–"} / ${r.load_expected_count ?? "–"}` : "—"}</td>
                  <td style={tdStyle}><Button variant="secondary" onClick={() => setDetailId(r.id)}>Ver</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {creating ? (
        <CreateRouteModal stores={stores} warehouses={warehouses} req={req} defaultDate={date}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); flash(`Recorrido #${id} creado`); loadRoutes(); setDetailId(id); }} />
      ) : null}
      {detailId !== null ? (
        <RouteDetailModal routeId={detailId} drivers={drivers} warehouses={warehouses} req={req} onClose={() => setDetailId(null)} onChanged={loadRoutes} />
      ) : null}
    </div>
  );
}

/* ===== Alta ===== */
type DraftStop = { type: "PICKUP" | "DELIVERY"; store_id?: number; warehouse_id?: number; window_seq?: number; delivery_ids?: number[]; label: string };

function CreateRouteModal({ stores, warehouses, req, defaultDate, onClose, onCreated }: { stores: Store[]; warehouses: WarehouseOption[]; req: (p: string, i?: any) => Promise<any>; defaultDate: string; onClose: () => void; onCreated: (id: number) => void }) {
  const [type, setType] = useState<RouteType>("PICKUP");
  const [date, setDate] = useState(defaultDate);
  const [capacity, setCapacity] = useState("");
  const [notes, setNotes] = useState("");
  const [stops, setStops] = useState<DraftStop[]>([]);
  const [storeId, setStoreId] = useState("");
  const [windowSeq, setWindowSeq] = useState("1");
  const crossdocks = warehouses.filter((w) => w.type === "CROSSDOCK");
  // Un Dropoff sin dirección y coordenadas puede existir, pero no sirve
  // como origen operativo de una colecta (ver informe "LYNX —
  // Multi-Warehouse, Dropoffs, Pickers y Estanterías", cierre de PR 6) --
  // se excluye acá para no ofrecerlo y que el 400 del backend sea la
  // primera noticia; el Crossdock de destino NO tiene esta restricción, el
  // flujo operativo de hoy no la necesita.
  const dropoffsTodos = warehouses.filter((w) => w.type === "DROPOFF");
  const dropoffs = dropoffsTodos.filter((w) => w.address && w.latitude != null && w.longitude != null);
  const dropoffsSinGeolocalizar = dropoffsTodos.length - dropoffs.length;
  // Destino operativo del recorrido (routes.warehouse_id): un Crossdock
  // activo, elegible, nunca hardcodeado -- con uno solo (el caso de hoy) se
  // preselecciona para no agregar un paso extra, pero el campo es real y
  // editable (ver informe "LYNX — Multi-Warehouse, Dropoffs, Pickers y
  // Estanterías", PR 6).
  const [destinoWarehouseId, setDestinoWarehouseId] = useState("");
  useEffect(() => {
    if (destinoWarehouseId || !crossdocks.length) return;
    if (crossdocks.length === 1) setDestinoWarehouseId(String(crossdocks[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossdocks.length]);
  const [dropoffWarehouseId, setDropoffWarehouseId] = useState("");
  const [attachable, setAttachable] = useState<Attachable[]>([]);
  const [shelves, setShelves] = useState<ShelfOption[]>([]);
  const [shelfId, setShelfId] = useState("");
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (type === "PICKUP") { setAttachable([]); return; }
    // El depósito arma el reparto mirando un estante: con estantería elegida
    // solo entra lo que está físicamente ahí.
    const q = shelfId ? `?type=${type}&shelf_id=${shelfId}` : `?type=${type}`;
    req(`/v2/routes/attachable${q}`).then((j) => setAttachable(j.deliveries ?? [])).catch((e) => setError(e.message));
    setPicked(new Set());
  }, [type, shelfId, req]);

  useEffect(() => {
    if (type === "PICKUP") return;
    req("/v2/warehouse/shelves").then((j) => setShelves(j.shelves ?? [])).catch(() => setShelves([]));
  }, [type, req]);

  const addPickup = () => {
    const s = stores.find((x) => x.id === Number(storeId));
    if (!s) return;
    setStops((prev) => [...prev, { type: "PICKUP", store_id: s.id, window_seq: Number(windowSeq) || 1, label: `Retiro · ${s.nombre_fantasia}${Number(windowSeq) > 1 ? ` (pasada ${windowSeq})` : ""}` }]);
  };
  // Parada de retiro con origen Dropoff (PR 6): misma lista `stops`, mismo
  // orden editable con mover arriba/abajo -- combinarla con paradas Store en
  // cualquier orden no necesita ninguna rama nueva, addStop() ya resuelve
  // cada origen por separado del lado del servidor.
  const addDropoffStop = () => {
    const w = dropoffs.find((x) => x.id === Number(dropoffWarehouseId));
    if (!w) return;
    setStops((prev) => [...prev, { type: "PICKUP", warehouse_id: w.id, label: `Retiro · ${w.name} (Dropoff)` }]);
  };
  // Cada envío elegido es una parada; los que comparten dirección se agrupan en una.
  const addDropoffs = () => {
    const chosen = attachable.filter((d) => picked.has(d.id));
    const byAddress = new Map<string, Attachable[]>();
    for (const d of chosen) { const k = `${(d.direccion ?? "").trim().toLowerCase()}|${d.codigo_postal ?? ""}`; byAddress.set(k, [...(byAddress.get(k) ?? []), d]); }
    setStops((prev) => [...prev, ...[...byAddress.values()].map((g) => ({ type: "DELIVERY" as const, delivery_ids: g.map((d) => d.id), label: `Entrega · ${g[0].direccion ?? "sin dirección"} (${g.length})` }))]);
    setAttachable((prev) => prev.filter((d) => !picked.has(d.id)));
    setPicked(new Set());
  };
  const removeStop = (i: number) => setStops((prev) => prev.filter((_, k) => k !== i));
  const move = (i: number, dir: -1 | 1) => setStops((prev) => { const n = [...prev]; const j = i + dir; if (j < 0 || j >= n.length) return prev; [n[i], n[j]] = [n[j], n[i]]; return n; });

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const json = await req("/v2/routes", { method: "POST", body: {
        type, scheduled_date: date, capacity: capacity ? Number(capacity) : null, notes: notes || null,
        warehouse_id: type !== "TURBO" && destinoWarehouseId ? Number(destinoWarehouseId) : null,
        stops: stops.map(({ label, ...s }) => s),
      } });
      onCreated(json.route.id);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  const visible = attachable.filter((d) => !filter || [d.tracking, d.nombre_destinatario, d.direccion, d.codigo_postal, d.localidad, d.seller].join(" ").toLowerCase().includes(filter.toLowerCase()));
  const allowsPickup = type !== "DELIVERY", allowsDropoff = type !== "PICKUP";

  return (
    <Modal title="Nuevo recorrido" onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
        <label style={{ display: "block" }}>
          <span style={{ ...smallMutedStyle, display: "block", marginBottom: "8px", fontWeight: 600 }}>Tipo</span>
          <select style={selectStyle} value={type} onChange={(e) => { setType(e.target.value as RouteType); setStops([]); }}>
            {(Object.keys(TYPE_LABEL) as RouteType[]).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
        </label>
        <Input label="Fecha" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Input label="Capacidad (bultos, opcional)" type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
        <Input label="Notas" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opcional" />
        {type !== "TURBO" ? (
          <label style={{ display: "block" }}>
            <span style={{ ...smallMutedStyle, display: "block", marginBottom: "8px", fontWeight: 600 }}>Destino (Crossdock)</span>
            <select style={selectStyle} value={destinoWarehouseId} onChange={(e) => setDestinoWarehouseId(e.target.value)}>
              <option value="">Sin destino</option>
              {crossdocks.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
        ) : null}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {allowsPickup ? (
            <div style={panelStyle}>
              <div style={{ ...smallMutedStyle, marginBottom: "8px", fontWeight: 600 }}>Agregar parada de retiro · Store</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 90px auto", gap: "8px", alignItems: "end" }}>
                <select style={selectStyle} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                  <option value="">Tienda…</option>
                  {stores.map((s) => <option key={s.id} value={s.id}>{s.nombre_fantasia}</option>)}
                </select>
                <Input label="Pasada" type="number" min={1} value={windowSeq} onChange={(e) => setWindowSeq(e.target.value)} />
                <Button variant="secondary" onClick={addPickup} disabled={!storeId}>Agregar</Button>
              </div>
              <div style={{ ...smallMutedStyle, marginTop: "6px" }}>El horario de corte vigente de la tienda se congela en la parada al crear el recorrido.</div>
            </div>
          ) : null}
          {allowsPickup && dropoffsTodos.length ? (
            <div style={panelStyle}>
              <div style={{ ...smallMutedStyle, marginBottom: "8px", fontWeight: 600 }}>Agregar parada de retiro · Dropoff</div>
              {dropoffs.length ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px", alignItems: "end" }}>
                  <select style={selectStyle} value={dropoffWarehouseId} onChange={(e) => setDropoffWarehouseId(e.target.value)}>
                    <option value="">Dropoff…</option>
                    {dropoffs.map((w) => <option key={w.id} value={w.id}>{w.name}{w.address ? ` · ${w.address}` : ""}</option>)}
                  </select>
                  <Button variant="secondary" onClick={addDropoffStop} disabled={!dropoffWarehouseId}>Agregar</Button>
                </div>
              ) : (
                <div style={smallMutedStyle}>Ningún Dropoff tiene dirección y coordenadas cargadas todavía (Sistema → Warehouses → Geolocalizar).</div>
              )}
              <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
                Se puede combinar con paradas Store en cualquier orden dentro del mismo recorrido.
                {dropoffsSinGeolocalizar > 0 ? ` ${dropoffsSinGeolocalizar} Dropoff(s) sin geolocalizar no se muestran acá.` : ""}
              </div>
            </div>
          ) : null}
          {allowsDropoff ? (
            <div style={panelStyle}>
              <div style={{ ...smallMutedStyle, marginBottom: "8px", fontWeight: 600 }}>
                Envíos disponibles para {type === "TURBO" ? "turbo (ingresados)" : "reparto (en planta o no entregados)"} · {attachable.length}
              </div>
              {shelves.length ? (
                <label style={{ display: "block", marginBottom: "8px" }}>
                  <span style={{ ...smallMutedStyle, display: "block", marginBottom: "6px", fontWeight: 600 }}>Origen</span>
                  <select style={selectStyle} value={shelfId} onChange={(e) => setShelfId(e.target.value)}>
                    <option value="">Todo el depósito</option>
                    {shelves.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name} ({s.deliveries_count})</option>)}
                  </select>
                </label>
              ) : null}
              <Input placeholder="Filtrar por tracking, destinatario, dirección, CP…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <div style={{ maxHeight: "260px", overflowY: "auto", marginTop: "8px" }}>
                {visible.length === 0 ? <div style={smallMutedStyle}>Nada para mostrar.</div> : visible.slice(0, 200).map((d) => (
                  <label key={d.id} style={{ display: "flex", gap: "10px", padding: "6px 0", borderBottom: "1px solid var(--border)", cursor: "pointer", color: "var(--text-primary)", fontSize: "13px" }}>
                    <input type="checkbox" checked={picked.has(d.id)} onChange={(e) => setPicked((p) => { const n = new Set(p); e.target.checked ? n.add(d.id) : n.delete(d.id); return n; })} />
                    <span style={{ fontFamily: "monospace" }}>{d.tracking ?? d.id}</span>
                    <span>{d.nombre_destinatario ?? "—"}</span>
                    <span style={smallMutedStyle}>
                      {d.direccion} · CP {d.postal_code_norm ?? d.codigo_postal} · {OPS_LABEL[d.ops_status] ?? d.ops_status}
                      {d.shelf_code ? ` · ${d.shelf_code}` : ""}
                      {d.geo_status === "pending" ? " · ⚠ falta ubicación" : ""}
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ marginTop: "8px" }}><Button variant="secondary" onClick={addDropoffs} disabled={picked.size === 0}>Agregar {picked.size || ""} como paradas</Button></div>
            </div>
          ) : null}
        </div>

        <div style={panelStyle}>
          <div style={{ ...smallMutedStyle, marginBottom: "8px", fontWeight: 600 }}>Paradas en orden · {stops.length}</div>
          {stops.length === 0 ? <div style={smallMutedStyle}>Todavía no hay paradas. Se pueden agregar después también.</div> : null}
          {stops.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 0", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", fontSize: "14px" }}>
              <span style={{ ...smallMutedStyle, width: "22px" }}>{i + 1}</span>
              <span style={{ flex: 1 }}>{s.label}</span>
              <Button variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
              <Button variant="ghost" onClick={() => move(i, 1)} disabled={i === stops.length - 1}>↓</Button>
              <Button variant="ghost" onClick={() => removeStop(i)}>✕</Button>
            </div>
          ))}
        </div>
      </div>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", marginTop: "12px" }}>{error}</div> : null}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={submit} disabled={saving || !date}>{saving ? "Creando…" : "Crear recorrido"}</Button>
      </div>
    </Modal>
  );
}

/* ===== Detalle ===== */
function RouteDetailModal({ routeId, drivers, warehouses, req, onClose, onChanged }: { routeId: number; drivers: Driver[]; warehouses: WarehouseOption[]; req: (p: string, i?: any) => Promise<any>; onClose: () => void; onChanged: () => void }) {
  const [route, setRoute] = useState<RouteDetail | null>(null);
  const [driverId, setDriverId] = useState("");
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pendientes por parada de retiro (PR 6): se piden a demanda, no de
  // entrada -- mismo endpoint para origen Store o Dropoff (GET /:id/stops/
  // :stopId/pending), la rama la resuelve el servidor según la parada.
  const [pendingByStop, setPendingByStop] = useState<Record<number, PendingItem[] | "loading" | null>>({});
  const verPendientes = async (stopId: number) => {
    if (pendingByStop[stopId]) { setPendingByStop((p) => ({ ...p, [stopId]: null })); return; }
    setPendingByStop((p) => ({ ...p, [stopId]: "loading" }));
    try {
      const j = await req(`/v2/routes/${routeId}/stops/${stopId}/pending`);
      setPendingByStop((p) => ({ ...p, [stopId]: j.deliveries ?? [] }));
    } catch (e: any) {
      setError(e.message);
      setPendingByStop((p) => ({ ...p, [stopId]: null }));
    }
  };

  const load = useCallback(async () => {
    try {
      const j = await req(`/v2/routes/${routeId}`);
      setRoute(j.route); setDriverId(j.route.driver_id ? String(j.route.driver_id) : ""); setExpected(j.route.load_expected_count ?? "");
    } catch (e: any) { setError(e.message); }
  }, [req, routeId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<any>) => {
    setBusy(true); setError(null);
    try { await fn(); await load(); onChanged(); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  const editable = route ? ["PLANNED", "ASSIGNED"].includes(route.status) : false;
  const cancellable = route ? !["COMPLETED", "CANCELLED"].includes(route.status) : false;

  if (!route) return <Modal title={`Recorrido #${routeId}`} onClose={onClose}>{error ? <div style={{ color: "var(--danger)" }}>{error}</div> : <span style={smallMutedStyle}>Cargando…</span>}</Modal>;

  return (
    <Modal title={<span>Recorrido #{route.id} · {TYPE_LABEL[route.type]} · <span style={badge(statusColor[route.status])}>{STATUS_LABEL[route.status]}</span></span>} onClose={onClose} width="1000px">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", marginBottom: "16px" }}>
        <div style={panelStyle}>
          <div style={smallMutedStyle}>Fecha · capacidad</div>
          <div style={{ marginTop: "6px", color: "var(--text-primary)" }}>{route.scheduled_date?.slice(0, 10)} · {route.capacity ?? "sin tope"}</div>
          <div style={{ ...smallMutedStyle, marginTop: "6px" }}>Inicio {fmtTime(route.started_at)} · fin {fmtTime(route.completed_at)}</div>
          <div style={{ ...smallMutedStyle, marginTop: "6px", fontFamily: "monospace", wordBreak: "break-all" }}>QR {route.qr_token}</div>
          <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
            Destino: {route.warehouse_id ? (warehouses.find((w) => w.id === route.warehouse_id)?.name ?? `Warehouse ${route.warehouse_id}`) : "sin destino"}
          </div>
        </div>
        <div style={panelStyle}>
          <div style={smallMutedStyle}>Chofer</div>
          <DriverPicker drivers={drivers} value={driverId} onChange={setDriverId} disabled={!editable || busy} />
          {editable ? <div style={{ marginTop: "8px" }}><Button variant="secondary" disabled={busy} onClick={() => act(() => req(`/v2/routes/${route.id}/driver`, { method: "PUT", body: { driver_id: driverId ? Number(driverId) : null } }))}>Guardar chofer</Button></div> : <div style={{ ...smallMutedStyle, marginTop: "6px" }}>{driverName(route.driver)}</div>}
        </div>
        {route.type === "DELIVERY" ? (
          <div style={panelStyle}>
            <div style={smallMutedStyle}>Control de carga</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px", alignItems: "end", marginTop: "6px" }}>
              <Input label="Bultos contados por el depósito" type="number" min={0} value={expected} onChange={(e) => setExpected(e.target.value)} disabled={!editable || busy} />
              {editable ? <Button variant="secondary" disabled={busy || expected === ""} onClick={() => act(() => req(`/v2/routes/${route.id}/expected-count`, { method: "PUT", body: { load_expected_count: Number(expected) } }))}>Guardar</Button> : null}
            </div>
            <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
              Chofer confirmó: {route.load_confirmed_count ?? "todavía no"}{route.load_confirmed_count !== null && route.load_expected_count !== null && route.load_confirmed_count !== route.load_expected_count ? <span style={{ color: "var(--danger)" }}> · diferencia {route.load_confirmed_count - route.load_expected_count}</span> : null}
            </div>
          </div>
        ) : null}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead><tr><th style={thStyle}>#</th><th style={thStyle}>Parada</th><th style={thStyle}>Estado</th><th style={thStyle}>Corte</th><th style={thStyle}>Llegó</th><th style={thStyle}>Envíos</th></tr></thead>
          <tbody>
            {route.stops.map((s) => (
              <tr key={s.id}>
                <td style={tdStyle}>{s.sequence}</td>
                <td style={tdStyle}>
                  {s.type === "PICKUP" ? (
                    s.warehouse_id ? (
                      <><strong>Retiro</strong> · {s.warehouse_name ?? `Warehouse ${s.warehouse_id}`} <span style={smallMutedStyle}>(Dropoff)</span>
                        <div style={smallMutedStyle}>
                          {s.store_address ?? ""}
                          {s.latitude_snapshot != null && s.longitude_snapshot != null ? ` · (${s.latitude_snapshot.toFixed(5)}, ${s.longitude_snapshot.toFixed(5)})` : " · ⚠ sin coordenadas"}
                        </div></>
                    ) : (
                      <><strong>Retiro</strong> · {s.store_name ?? `tienda ${s.store_id}`}<div style={smallMutedStyle}>{s.store_address ?? ""}{s.window_snapshot && s.window_snapshot > 1 ? ` · pasada ${s.window_snapshot}` : ""}</div></>
                    )
                  ) : <><strong>Entrega</strong> · {s.address ?? "—"}<div style={smallMutedStyle}>CP {s.postal_code ?? "—"} · {s.receiver_name ?? ""}</div></>}
                </td>
                <td style={tdStyle}>{STOP_STATUS_LABEL[s.status] ?? s.status}</td>
                <td style={tdStyle}>{s.type === "PICKUP" && !s.warehouse_id ? (s.cutoff_snapshot ? s.cutoff_snapshot.slice(0, 5) : <span style={smallMutedStyle}>sin horario</span>) : "—"}</td>
                <td style={tdStyle}>{fmtTime(s.arrived_at)}{s.type === "PICKUP" && s.store_ready_at ? <div style={smallMutedStyle}>tienda lista {fmtTime(s.store_ready_at)}</div> : null}</td>
                <td style={tdStyle}>
                  {s.deliveries.length === 0 ? <span style={smallMutedStyle}>{s.type === "PICKUP" ? `${s.collected_count ?? 0} colectados` : "—"}</span> : s.deliveries.map((d) => (
                    <div key={d.delivery_id} style={{ display: "flex", gap: "8px", alignItems: "center", fontSize: "13px" }}>
                      <span style={{ fontFamily: "monospace" }}>{d.tracking ?? d.delivery_id}</span>
                      <span>{d.nombre_destinatario ?? ""}</span>
                      <span style={smallMutedStyle}>{OPS_LABEL[d.ops_status] ?? d.ops_status}{d.outcome ? ` · ${d.outcome}` : ""}</span>
                      {editable && s.type === "DELIVERY" ? <Button variant="ghost" disabled={busy} onClick={() => act(() => req(`/v2/routes/${route.id}/stops/${s.id}/deliveries/${d.delivery_id}`, { method: "DELETE" }))}>quitar</Button> : null}
                    </div>
                  ))}
                  {s.type === "PICKUP" ? (
                    <div style={{ marginTop: "6px" }}>
                      <Button variant="ghost" onClick={() => verPendientes(s.id)}>
                        {pendingByStop[s.id] ? "Ocultar pendientes" : "Ver pendientes"}
                      </Button>
                      {pendingByStop[s.id] === "loading" ? <div style={smallMutedStyle}>Cargando…</div> : null}
                      {Array.isArray(pendingByStop[s.id]) ? (
                        (pendingByStop[s.id] as PendingItem[]).length === 0 ? (
                          <div style={smallMutedStyle}>Nada pendiente de retiro ahí.</div>
                        ) : (pendingByStop[s.id] as PendingItem[]).map((p) => (
                          <div key={p.delivery_id} style={{ ...smallMutedStyle, display: "flex", gap: "8px" }}>
                            <span style={{ fontFamily: "monospace" }}>{p.tracking ?? p.delivery_id}</span>
                            <span>{p.nombre_destinatario ?? ""}</span>
                            <span>{OPS_LABEL[p.ops_status] ?? p.ops_status}</span>
                          </div>
                        ))
                      ) : null}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", marginTop: "12px" }}>{error}</div> : null}
      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", marginTop: "16px", flexWrap: "wrap" }}>
        <div style={smallMutedStyle}>{route.notes ?? ""}</div>
        <div style={{ display: "flex", gap: "10px" }}>
          {cancellable ? <Button variant="danger" disabled={busy} onClick={() => { if (window.confirm("¿Cancelar el recorrido? Los envíos sin resultado vuelven a planta.")) act(() => req(`/v2/routes/${route.id}/status`, { method: "PUT", body: { status: "CANCELLED" } })); }}>Cancelar recorrido</Button> : null}
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
        </div>
      </div>
    </Modal>
  );
}

